import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, Clock, ArrowRight, Layers } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const DIFF_COLORS = {
  Guided: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
  Assisted: "bg-blue-500/10 text-blue-600 dark:text-blue-400",
  Challenge: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
  "Production Scenario": "bg-rose-500/10 text-rose-600 dark:text-rose-400",
  Capstone: "bg-purple-500/10 text-purple-600 dark:text-purple-400",
};

export default function Curriculum() {
  const [levels, setLevels] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/curriculum").then((r) => setLevels(r.data.levels)).catch(() => {});
  }, []);

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <div className="flex items-center gap-3">
        <Layers className="h-6 w-6 text-primary" />
        <h1 className="font-display font-bold text-3xl tracking-tight">Curriculum</h1>
      </div>
      <p className="text-muted-foreground mt-1">Progress from Linux fundamentals to advanced administration.</p>

      <div className="mt-8 space-y-10">
        {levels.map((lv) => (
          <section key={lv.id}>
            <div className="flex items-baseline gap-3 mb-4">
              <span className="font-mono text-sm text-muted-foreground">L{lv.id}</span>
              <h2 className="font-display font-bold text-xl tracking-tight">{lv.title}</h2>
              <span className="text-sm text-muted-foreground">— {lv.subtitle}</span>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {lv.labs.map((lab) => (
                <div
                  key={lab.id}
                  data-testid={`lab-card-${lab.id}`}
                  className="group rounded-lg border border-border bg-card p-5 hover:border-primary/50 hover:-translate-y-0.5 transition-[transform,border-color] cursor-pointer"
                  onClick={() => navigate(`/lab/${lab.id}`)}
                >
                  <div className="flex items-start justify-between gap-3">
                    <span className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-1 rounded ${DIFF_COLORS[lab.difficulty] || ""}`}>
                      {lab.difficulty}
                    </span>
                    {lab.completed && (
                      <span className="flex items-center gap-1 text-xs text-success font-medium">
                        <CheckCircle2 className="h-4 w-4" /> {lab.best_score}%
                      </span>
                    )}
                  </div>
                  <h3 className="font-display font-bold text-lg mt-3 tracking-tight">{lab.title}</h3>
                  <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{lab.scenario}</p>
                  <div className="flex items-center justify-between mt-4">
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {lab.minutes}m</span>
                      <span className="font-mono">+{lab.xp} XP</span>
                    </div>
                    <Button variant="ghost" size="sm" className="gap-1 text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                      {lab.completed ? "Practice" : "Start"} <ArrowRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
